import React, { memo, useCallback, useEffect } from "react";
import { useImporter } from "../../contexts/importer-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useFeatureFlagToggle } from "../../hooks/useFeatureFlags";
import { useLabels } from "../../hooks/useLabels";
import { usePhotoSourceDialogAsync } from "../dialogs/photo-source-dialog";
import { showRejectedPhotosDialogAsync } from "../dialogs/rejected-photos-dialog";
import { IconButton } from "../icon-button";
import { ImportPhotosPopover } from "../import-photos-popover/import-photos-popover";
import "requestidlecallback-polyfill";

import { usePhotoPreprocessProgress } from "../../hooks/use-photo-preprocess-progress";
import { useGoogleAnalytics } from "../../hooks/useGoogleAnalytics";
import { labels } from "./labels";
import { preloadAsync } from "../dialogs/photo-source-dialog/photo-source-dialog-container";
import { useIsAuthenticated } from "../../hooks/useIsAuthenticated";
import { IconName } from "../icon";

/**
 * Returns a function to preprocess and upload photo
 * Extract EXIF, make thumb, medium, calculate hash and upload
 */
function usePhotoPreprocessor() {
	const { context, editor, client, platform, logger } = useAppContext();

	const { jsUrl, maxConcurrentPreprocessings } = context;

	const importer = useImporter();
	const uploader = importer?.uploader;
	const photoDictionary = editor.definition ? editor.assets.photos : undefined;

	const [, setPreprocessProgress] = usePhotoPreprocessProgress();

	// Place in Context?
	const imageSizes = eXpress.creator.ImageSizeRules.defaultImageSizes;

	const uploadChaos = useFeatureFlagToggle("debugUploadChaos");
	const alwaysUpload = useFeatureFlagToggle("debugAlwaysUpload");
	const showStepsVisualizer = useFeatureFlagToggle("debugUploadDetails");

	const getExifAsync = useCallback(async (buffer: ArrayBuffer) => {
		// No need to catch the error, server will fetch the exif for us during upload
		const { ExifParserFactory } = await import(
			/* webpackChunkName: "ts-exif-parser" */
			"ts-exif-parser"
		);

		const parser = ExifParserFactory.create(buffer);

		const { DateTimeOriginal: timestamp, Orientation: orientation = 0 } =
			parser.parse().tags || {};

		return {
			dateTaken: timestamp ? new Date(timestamp * 1000) : undefined,
			orientation,
		};
	}, []);

	return useCallback(
		async ({ files }: { files: File[] }) => {
			const configs = await client.getConfigsAsync([
				{
					key: "Upload.FileSizeLimit",
				},
			]);

			const maxUploadFileSizeInKiloBytes = configs.get("Upload.FileSizeLimit") ?? 40 * 1024;
			const maxUploadFileSizeInBytes = +maxUploadFileSizeInKiloBytes * 1024;

			const requiredPhotoDictionary = eXpress.utils.required(
				photoDictionary,
				"Photo dictionary is not loaded, definition needs to be available",
			);

			return eXpress.creator.library
				.processLocalPhotosAsync(
					{
						files,
						maxConcurrentPreprocessings,
						maxUploadFileSizeInBytes,
						imageSizes,
						uploadChaos,
						showStepsVisualizer,
						alwaysUpload,
						onProgress: (progressCount, total) => {
							setPreprocessProgress({
								total,
								progressCount,
							});
						},
					},
					{
						platform,
						logger,
						photoDictionary: requiredPhotoDictionary,
						getPhotosByHashAsync: client.getPhotosByHash.bind(client),
						getPhotoImageRefAsync: client.getPhotoImageRefsAsync.bind(client),
						logErrorAsync: client.logErrorAsync.bind(client),
						uploadFileAsync: uploader?.uploadPhotoAsync.bind(uploader),
						jsUrl,
						getAccountId: () => context.account?.id,
						getExifAsync,
					},
				)
				.then((items) => {
					if (!items) {
						return undefined;
					}

					setPreprocessProgress(undefined);
					const errors = eXpress.creator.validateFileUpload(items);
					return errors
						.reduce(
							(previousPromise, { formatMessage, code }) =>
								previousPromise.then(() =>
									showRejectedPhotosDialogAsync(code, formatMessage),
								),
							Promise.resolve<false | undefined>(undefined),
						)
						.then(() => items.map((item) => item.photo).filter((item) => !!item));
				});
		},
		[
			alwaysUpload,
			client,
			context.account?.id,
			photoDictionary,
			getExifAsync,
			imageSizes,
			jsUrl,
			logger,
			maxConcurrentPreprocessings,
			platform,
			setPreprocessProgress,
			showStepsVisualizer,
			uploadChaos,
			uploader,
		],
	);
}

/** Show photo source dialog and upload/download to server */
export function useImportPhotosDialog() {
	const { editor, client, platform, googleAnalytics } = useAppContext();

	const uploadPhotoAsync = usePhotoPreprocessor();

	const showPhotoSourceDialogAsync = usePhotoSourceDialogAsync();

	// Preload required photo stores
	const isAuthenticated = useIsAuthenticated();
	useEffect(() => {
		const id = requestIdleCallback(() => {
			void preloadAsync(client, isAuthenticated());
		}, {});
		return () => cancelIdleCallback(id);
	}, [client, isAuthenticated]);

	return useCallback(
		() =>
			showPhotoSourceDialogAsync().then((photoSourceSelection) => {
				if (!photoSourceSelection) return undefined;

				const photos =
					"files" in photoSourceSelection
						? photoSourceSelection.files
						: "smartphotoPhotos" in photoSourceSelection
						? photoSourceSelection.smartphotoPhotos
						: "partnerPhotos" in photoSourceSelection
						? photoSourceSelection.partnerPhotos
						: "pickerPhotos" in photoSourceSelection
						? photoSourceSelection.pickerPhotos
						: [];

				const photoCount = photos.length;

				// Remark: Number logged is number of selected photos for upload or download, without duplicate check
				googleAnalytics.trackEventToGa({
					trackingAction: "UploadPhotosCount",
					eventValue: photoCount,
				});

				// Local files
				if ("files" in photoSourceSelection) {
					const files = photoSourceSelection.files;
					return uploadPhotoAsync({ files });
				}

				// Smartphoto photos
				if ("smartphotoPhotos" in photoSourceSelection) {
					return (
						photoSourceSelection.smartphotoPhotos
							// Remove already imported photos
							.filter(
								(selectedPhoto) =>
									!editor.assets.photoSelection.find(
										(photo) => photo.name === selectedPhoto.name,
									),
							)
							// Convert to Photo object
							.map((photoSourceItem) => {
								// Use the correct PhotoReferenceId
								const fileId =
									(photoSourceItem as eXpress.client.SmartPhotoSourceItem)
										.fileId ?? platform.guidString.create();

								return new eXpress.core.Photo(
									photoSourceItem,
									editor.assets.photos,
									fileId,
								);
							})
					);
				}

				// Partner Photos
				if ("partnerPhotos" in photoSourceSelection) {
					return (
						photoSourceSelection.partnerPhotos
							// Remove already imported photos
							.filter(
								(selectedPhoto) =>
									// Confusing: 'code' field in partner selection becomes 'name' in Photo
									!editor.assets.photoSelection.find(
										(photo) => photo.name === selectedPhoto.name,
									),
							)
							// Convert to Photo object
							.map(
								(photoSourceItem) =>
									new eXpress.core.Photo(
										photoSourceItem,
										editor.assets.photos,
										platform.guidString.create(),
									),
							)
					);
				}

				// Picker Photos
				if ("pickerPhotos" in photoSourceSelection) {
					return (
						photoSourceSelection.pickerPhotos
							// Remove already imported photos
							.filter(
								(selectedPhoto) =>
									!editor.assets.photoSelection.find(
										(photo) => photo.name === selectedPhoto.name,
									),
							)
							// Convert to Photo object
							.map(
								(selectedPhoto) =>
									new eXpress.core.Photo(
										selectedPhoto,
										editor.assets.photos,
										platform.guidString.create(),
									),
							)
					);
				}

				return [];
			}),
		[
			showPhotoSourceDialogAsync,
			googleAnalytics,
			uploadPhotoAsync,
			editor,
			platform.guidString,
		],
	);
}

export function useImportPhotosButton() {
	const { editor } = useAppContext();

	const [photoPreprocessProgress] = usePhotoPreprocessProgress();
	const selectPhotosDialogAsync = useImportPhotosDialog();
	const importer = useImporter();

	// Prevent a new preprocessing when already busy
	const isPreprocessing = !!photoPreprocessProgress;

	const { createErrorHandler } = useErrorHandling();

	return {
		disabled: isPreprocessing,
		iconName: "plus-circle" as IconName,
		onClick: () => {
			if (isPreprocessing) return;
			selectPhotosDialogAsync()
				.then((selectedPhotos) => {
					if (selectedPhotos && selectedPhotos.length) {
						editor.addToPhotoSelection(selectedPhotos, (photos) => {
							importer?.addToQueue(photos);
						});
					}
					return undefined;
				})
				.catch(createErrorHandler());
		},
	};
}

/** A popover for the type of import is automatically added (Local/Partner vs AI) */
export const ImportPhotosButton = memo(
	({
		containerClassName,
		useShortTitle = true,
		showTitle = true,
	}: {
		containerClassName?: "PhotoSelectionScreenContainer" | "PhotoSelectionPanel";
		useShortTitle?: boolean;
		showTitle?: boolean;
	}) => {
		// Prevent a new preprocessing when already busy
		const googleAnalytics = useGoogleAnalytics();

		const [[title]] = useLabels([
			showTitle
				? useShortTitle
					? labels.addMorePhotos
					: labels.tapToAddMorePhotos
				: undefined,
		]);

		const { onClick: importPhotos, disabled } = useImportPhotosButton() ?? {};

		const trackAnalytics = () => {
			switch (containerClassName) {
				case "PhotoSelectionScreenContainer":
					googleAnalytics.trackEventToGa({
						trackingAction: "EPSAddMoreClicked",
					});
					break;
				case "PhotoSelectionPanel":
				case undefined:
				default:
					googleAnalytics.trackEventToGa({
						trackingAction: "AddMoreClicked",
					});
			}
		};

		return (
			<ImportPhotosPopover
				onImportPhotos={() => {
					importPhotos?.();
					trackAnalytics();
				}}
				render={(props) => (
					<IconButton
						disabled={disabled}
						iconName="plus-circle"
						title={showTitle ? title : undefined}
						{...props}
					/>
				)}
			/>
		);
	},
);
ImportPhotosButton.displayName = "ImportPhotosButton";
